import { getCompanyURL } from '@general/entities/company';
import { getMemberDesignations, getMemberFullName, getMemberURL } from '@general/entities/member';
import { transformLocation } from '@general/location';

export const mapSearchMemberToProps = ({ hit, isFavorite, onClick }) => {
    const id = parseInt(hit.objectID.split('-')[1], 10);
    // temp fixes for saved searches indexes using camelcase
    const firstName = hit.firstName ? hit.firstName : hit.first_name;
    const lastName = hit.lastName ? hit.lastName : hit.last_name;
    const graphID = hit.graphID ? hit.graphID : hit.graphId;
    const companyID = hit.companyID ? hit.companyID : hit.company_id;

    return {
        id,
        graphID,
        firstName,
        lastName,
        fullName: getMemberFullName(firstName, lastName, hit.suffix),
        // remove title eventually, it's being replace in prod with member_title
        title: hit.member_title || hit.title,
        suffix: hit.suffix,
        displayLocation: transformLocation(hit.location),
        companyName: hit.company_name,
        designations: getMemberDesignations(hit.designations),
        businessType: hit.business_type,
        meetingInvites: !!hit.meeting_invites,
        photoURL: hit.photo_url,
        url: getMemberURL(id),
        companyID,
        companyURL: getCompanyURL(companyID),
        createdAt: String(hit.created_at_timestamp),
        isFavorite,
        onClick,
        status: hit.status,
    };
};

export const mapAlgoliaSavedSearchableMemberToProps = ({ hit, isFavorite, onClick }) => {
    const id = parseInt(hit.objectID.split('-')[0], 10);

    return {
        id,
        graphID: hit.memberId,
        firstName: hit.firstName,
        lastName: hit.lastName,
        fullName: getMemberFullName(hit.firstName, hit.lastName, hit.suffix),
        // remove title eventually, it's being replace in prod with member_title
        title: hit.memberTitle,
        suffix: hit.suffix,
        displayLocation: transformLocation({ city: hit.city, state: hit.state, country: hit.country }),
        companyName: hit.companyName,
        designations: getMemberDesignations(hit.designations),
        businessType: hit.businessType,
        meetingInvites: hit.meetingInvites === '1' || hit.meetingInvites === 1,
        photoURL: hit.photoUrl,
        url: getMemberURL(id),
        companyURL: getCompanyURL(hit.company_id),
        isFavorite,
        onClick,
        status: hit.status,
    };
};

// graphQL
export const mapGraphMemberToProps = member => ({
    id: member.recordId,
    graphID: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    fullName: getMemberFullName(member.firstName, member.lastName, member.suffix),
    title: member.title,
    displayLocation: transformLocation(member.address),
    companyName: member.company ? member.company.name : null,
    companyID: member.company ? member.company.recordId : null,
    companyURL: getCompanyURL(member.company ? member.company.recordId : null),
    photoURL: member.photoUrl,
    // not in graphql
    designations: getMemberDesignations([]),
    businessType: [],
    url: getMemberURL(member.recordId),
    isFavorite: typeof member.isFavorite !== 'undefined' ? member.isFavorite : true,
    status: member.status,
});

export const mapSavedSearchableMemberToProps = ({ hit, isFavorite, onClick }) => {
    const id = parseInt(hit.memberId);
    return {
        id,
        graphID: hit.graphId,
        firstName: hit.firstName,
        lastName: hit.lastName,
        fullName: getMemberFullName(hit.firstName, hit.lastName, hit.suffix),
        title: hit.member_title,
        suffix: hit.suffix,
        displayLocation: transformLocation({
            address: hit.address,
            city: hit.city,
            state: hit.state,
            country: hit.country,
            stateCode: hit.stateCode,
            region: hit.region,
        }),
        companyName: hit.companyName,
        designations: getMemberDesignations(hit.designations),
        businessType: hit.businessType,
        photoURL: hit.photoUrl,
        url: getMemberURL(id),
        companyURL: getCompanyURL(hit.companyId),
        createdAt: null,
        isFavorite,
        onClick,
        status: hit.status,
    };
};

export const mapAlgoliaContactWithSavedSearchesToProps = ({ hit, isFavorite }) => {
    if (typeof hit.id === 'undefined' && hit?.objectID) {
        hit.id = parseInt(hit.objectID.split('-')[0], 10);
    }

    return {
        savedSearch: hit.savedSearch,
        member: hit,
        isFavorite,
    };
};

export const mapAPIMemberToProps = ({ member, preferDeferredCompany = false }) => {
    const transformedMember = {
        id: member.id || '',
        prospect: member.prospect,
        businessType: member.primary_business_type_object?.code ? member.primary_business_type_object.code : '',
        caslExpressConsent: member.casl_express_consent,
        cellPhone: member.cell_phone || '',
        designations: member.designations.join(', '),
        displayLocation: transformLocation(member.location),
        email: member.email || '',
        firstName: member.first_name || '',
        fullName: `${member.first_name} ${member.last_name}`,
        functionalTitle: member.functional_title || '',
        gdprConsent: member.gdpr_consent,
        hasBirthdate: member.has_birthdate,
        independent:
            member.deferred_independent ||
            (member.company == null && member.deferred_company == null && !member.prospect),
        lastName: member.last_name || '',
        nonmemberCompany: member.nonmember_company || '',
        personalResponsibility: member.personal_responsibility || '',
        photoURL: member.photo_url,
        secondaryEmail: member.secondary_email || '',
        smsUpdates: member.sms_optin,
        title: member.title || '',
        url: getMemberURL(member.id),
        workPhone: member.work_phone || '',
    };

    if (preferDeferredCompany) {
        transformedMember.companyID = member.deferred_company?.id || member.company?.id || null;
        transformedMember.companyName = member.deferred_company?.name || member.company?.name || '';
        transformedMember.nonmemberCompany =
            member.deferred_company?.name || member.company?.name || member.nonmember_company || '';
    } else {
        transformedMember.companyID = member.company?.id || member.deferred_company?.id || null;
        transformedMember.companyName = member.company?.name || member.deferred_company?.name || '';
        transformedMember.nonmemberCompany =
            member.company?.name || member.deferred_company?.name || member.nonmember_company || '';
    }

    const mainAddress = member.addresses.find(a => a.purpose === 'main');
    if (mainAddress) {
        transformedMember.mainAddressCountry = mainAddress.country || '';
        transformedMember.mainAddressAddress1 = mainAddress.address_1 || '';
        transformedMember.mainAddressAddress2 = mainAddress.address_2 || '';
        transformedMember.mainAddressAddress3 = mainAddress.address_3 || '';
        transformedMember.mainAddressCity = mainAddress.city;
        transformedMember.mainAddressState = mainAddress.state_province || '';
        transformedMember.mainAddressZip = mainAddress.zip || '';
        transformedMember.mainAddressPreferredBilling = mainAddress.preferred_billing || '';
        transformedMember.mainAddressPreferredMailing = mainAddress.preferred_mailing || '';

        let fullAddressBuilder = '';

        if (mainAddress.address_1) {
            fullAddressBuilder += `${mainAddress.address_1}`;
        }

        if (mainAddress.address_2) {
            if (fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.address_2}`;
        }

        if (mainAddress.address_3) {
            if (fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.address_3}`;
        }

        if (mainAddress.city) {
            if (fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.city}`;
        }

        if (mainAddress.state_province) {
            if (fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.state_province}`;
        }

        if (mainAddress.zip) {
            if (mainAddress.stateProvince) {
                fullAddressBuilder += ' ';
            }

            if (!mainAddress.stateProvince && fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.zip}`;
        }

        if (mainAddress.country) {
            if (fullAddressBuilder.length > 0) {
                fullAddressBuilder += ', ';
            }

            fullAddressBuilder += `${mainAddress.country}`;
        }

        transformedMember.mainAddressFull = fullAddressBuilder;
    }

    if (member.has_birthdate && member.birthdate) {
        const parts = member.birthdate.split('-');
        transformedMember.birthdateYear = parts[0]; // eslint-disable-line prefer-destructuring
        transformedMember.birthdateMonth = parts[1]; // eslint-disable-line prefer-destructuring
        transformedMember.birthdateDay = parts[2].substring(0, 2);
    }

    return transformedMember;
};
